// HYPPE AROMA v1.0.1 — GA4 Ecommerce analytics.
// Single reusable module. Every function is gtag-safe: if gtag has not loaded
// (blocked, offline, not yet initialised) tracking is skipped silently and never
// throws. GA4 spec: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
//
// gtag('config') runs standard in index.html (sends initial page_view); this module
// owns the ecommerce events only.

const CURRENCY = "INR";
const BRAND = "HYPPE AROMA";

const round2 = (n) => {
  const x = Number(n);
  return Number.isFinite(x) ? Math.round((x + Number.EPSILON) * 100) / 100 : 0;
};

const gtagReady = () => typeof window !== "undefined" && typeof window.gtag === "function";

const track = (event, params) => {
  try {
    if (!gtagReady()) return;
    window.gtag("event", event, params);
  } catch (_e) {
    /* analytics must never break the app */
  }
};

// Drop empty/undefined keys so we never send blank GA4 fields.
const clean = (obj) => {
  const out = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  });
  return out;
};

// Build a GA4 item from a storefront product + selection context (Product / Shop).
export const productToItem = (product, { name, familyLabel, price, variant, quantity } = {}) =>
  clean({
    item_id: String(product?.id ?? ""),
    item_name:
      name ||
      product?.name ||
      (product?.inspired_by ? `Inspired by ${product.inspired_by}` : ""),
    item_brand: BRAND,
    item_category: familyLabel || undefined,
    item_variant: variant || undefined,
    price: round2(price),
    quantity: quantity || 1,
  });

// Build a GA4 item from a cart line item (the object passed to addItem).
export const cartItemToItem = (it = {}) => {
  const category =
    it.product === "custom"
      ? "Signature Duet"
      : it.product === "discovery"
      ? "Discovery Box"
      : "Fragrance";
  const id =
    it.scent_id != null
      ? String(it.scent_id)
      : it.product === "custom"
      ? `duet-${it.scent_1}-${it.scent_2}`
      : it.product === "discovery"
      ? "discovery-box"
      : it.name || "item";
  return clean({
    item_id: id,
    item_name: it.name,
    item_brand: BRAND,
    item_category: category,
    item_variant: [it.concentration_label, it.size].filter(Boolean).join(" · ") || undefined,
    price: round2(it.price),
    quantity: Number(it.quantity) || 1,
  });
};

export const trackViewItem = ({ item }) =>
  track("view_item", { currency: CURRENCY, value: round2(item?.price), items: [item] });

export const trackSelectItem = ({ item, list }) =>
  track("select_item", clean({ item_list_name: list, items: [item] }));

export const trackAddToCart = (cartItem) => {
  const gi = cartItemToItem(cartItem);
  track("add_to_cart", { currency: CURRENCY, value: round2(gi.price * gi.quantity), items: [gi] });
};

export const trackRemoveFromCart = (cartItem) => {
  const gi = cartItemToItem(cartItem);
  track("remove_from_cart", { currency: CURRENCY, value: round2(gi.price * gi.quantity), items: [gi] });
};

export const trackViewCart = ({ value, items = [] }) =>
  track("view_cart", { currency: CURRENCY, value: round2(value), items: items.map(cartItemToItem) });

export const trackBeginCheckout = ({ value, items = [], coupon }) =>
  track(
    "begin_checkout",
    clean({ currency: CURRENCY, value: round2(value), coupon, items: items.map(cartItemToItem) })
  );

export const trackAddPaymentInfo = ({ value, items = [], coupon, payment_type }) =>
  track(
    "add_payment_info",
    clean({ currency: CURRENCY, value: round2(value), coupon, payment_type, items: items.map(cartItemToItem) })
  );

export const trackPurchase = ({ transaction_id, value, tax, shipping, coupon, items = [] }) =>
  track(
    "purchase",
    clean({
      currency: CURRENCY,
      transaction_id: transaction_id != null ? String(transaction_id) : undefined,
      value: round2(value),
      tax: round2(tax),
      shipping: round2(shipping),
      coupon,
      items: items.map(cartItemToItem),
    })
  );
